// ============================================================
// ANSICHTSSACHE-AGENT – Kennzeichen
// ============================================================
// Vorher trugen alle Varianten denselben grauen Grund und
// unterschieden sich nur in der Schriftfarbe — auf einer Liste
// mit zwanzig Zeilen sah damit jeder Zustand gleich aus. Jetzt
// traegt jeder Zustand seinen eigenen, sehr blassen Grund. Blass
// ist Absicht: das Kennzeichen soll auffindbar sein, nicht laut.
// ============================================================

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  [
    // Ein Kennzeichen bricht nie um und laesst sich nicht stauchen:
    // in einer schmalen Tabellenspalte stand „Im Entwurf“ sonst
    // zweizeilig. Den Platz gibt der Nachbar her, nicht das Zeichen.
    "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 whitespace-nowrap",
    "text-[10px] font-medium uppercase leading-none tracking-wider",
    // Ein Innenring statt eines Rahmens: er verschiebt nichts und
    // gibt der Flaeche im Dunkelmodus trotzdem eine Kante.
    "ring-1 ring-inset",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "bg-bg-tertiary text-text-secondary ring-border",
        secondary: "bg-bg-secondary text-text-tertiary ring-border-subtle",
        success: "bg-success-subtle text-success ring-success/20",
        warning: "bg-warning-subtle text-warning ring-warning/20",
        danger: "bg-danger-subtle text-danger ring-danger/20",
        accent: "bg-accent-subtle text-accent ring-accent/20",
        // Zwischen „warning“ (offen) und „success“ (erledigt):
        // umgesetzt, wartet auf Abnahme.
        review: "bg-review-subtle text-review ring-review/20",
        // Bezeichnet jemanden, statt etwas hervorzuheben — der
        // zweite Ton (`accent-2`), wie an den Personenkreisen.
        // Rot bleibt dem Alarm, Gruen der Freigabe.
        person: "bg-accent-2-subtle text-accent-2 ring-accent-2/20",
        outline: "bg-transparent text-text-tertiary ring-border",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
